import { api as wiredApi, tokenStore as wiredTokenStore } from './index';

/**
 * A client's account on InGo's API (the mobile plan's M3, the app's half;
 * InGo ADR 0023): signing in and out, and registering. The token goes to the
 * secure store here and nowhere else. The old screens compared the password
 * in the browser against a column any visitor could read; this sends it once,
 * over TLS, to a server that keeps only its hash.
 *
 * Built from its two collaborators so a test can hand it fakes, as
 * `riders.js` is; `customerApi` below is the app's wired copy.
 */
export function createCustomerApi({ api, tokenStore }) {
  /** Stores the token first: an account without its token is not a session. */
  async function begin(data) {
    await tokenStore.write(data.token);

    return { account: data.account, mustChangePassword: Boolean(data.must_change_password) };
  }

  return {
    /** `POST customer/auth/login`. A wrong password, an unknown address and a closed account answer alike. */
    async signIn(email, password) {
      const { data } = await api.post('/customer/auth/login', { email, password });

      return begin(data);
    },

    /** `POST customer/registrations`: the account made at once, signed in. */
    async register(details) {
      const { data } = await api.post('/customer/registrations', details);

      return begin(data);
    },

    /**
     * `PATCH customer/auth/password`: a client imported from the old app
     * replaces the password it brought before anything else opens (D15; the
     * server walls every other client route until then). Other sessions are
     * signed out; this one is kept.
     */
    async choosePassword(currentPassword, password) {
      await api.patch('/customer/auth/password', {
        current_password: currentPassword,
        password,
        password_confirmation: password,
      });
    },

    /** `GET customer/me`: the client's own details, as sign-in answers them (ingo-e0, 2 Oct 2026). */
    async me() {
      const { data } = await api.get('/customer/me');

      return data;
    },

    /**
     * `PATCH customer/me`: only the fields given change. A new email needs
     * `current_password`; an address or number someone else holds is refused
     * naming nobody (`DETAIL_TAKEN`). Answers the account as it now stands.
     */
    async updateMe(changes) {
      const { data, message } = await api.patch('/customer/me', changes);

      return { account: data, message };
    },

    /**
     * `POST customer/auth/deletion` (InGo ADR 0038): the account is deleted
     * thirty days from now unless its holder keeps it from the emailed link.
     * Confirmed by `{ password }`, or `{ email }` (the account's own address,
     * for an account made with Google). The server ends every session, so the
     * token is forgotten here; the server's sentence names the day.
     */
    async requestDeletion(confirmation) {
      const { data, message } = await api.post('/customer/auth/deletion', confirmation);
      await tokenStore.clear();

      return { dueOn: data?.due_on ?? null, message };
    },

    /** Ends this device's session on the server, then here, whatever the server said. */
    async signOut() {
      try {
        await api.post('/customer/auth/logout', {});
      } catch {
        // Offline or already refused: the token is forgotten below either way.
      } finally {
        await tokenStore.clear();
      }
    },
  };
}

export const customerApi = createCustomerApi({ api: wiredApi, tokenStore: wiredTokenStore });
